import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  Table,
  Empty,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Alert,
  Divider,
  Tabs,
  Grid
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ColumnHeightOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import ConditionBuilder from '../../../shared/components/cdn/ConditionBuilder';
import ActionBuilder, { serializeActions, validateActions, ACTION_LABELS } from '../../../shared/components/cdn/ActionBuilder';
import { planAllows } from '../../../shared/components/cdn/PlanGate';
import { billingService } from '../../services/billingService';

const { useBreakpoint } = Grid;

export default function PageRules() {
  const { t } = useTranslation();
  const { domainData, reloadDomain, subscriptionInfo } = useOutletContext();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [rules, setRules] = useState([]);
  const [form] = Form.useForm();
  const [matcher, setMatcher] = useState({});
  const [action, setAction] = useState({});
  const [resourceQuota, setResourceQuota] = useState(null);

  useEffect(() => {
    loadRules();
    loadQuota();
  }, [domainData, subscriptionInfo]);

  const loadRules = async () => {
    setLoading(true);
    try {
      const response = await cdnService.getRules(domainData.id);

      if (response.success) {
        setRules(response.data || []);
      } else {
        message.error(response.error || t('Failed to load rules'));
      }
    } catch (error) {
      console.error('Failed to load rules:', error);
      message.error(t('Failed to load rules'));
    } finally {
      setLoading(false);
    }
  };

  const loadQuota = async () => {
    if (subscriptionInfo?.resource_quota_usage?.domains?.[domainData.id]?.quotas) {
      setResourceQuota(subscriptionInfo.resource_quota_usage.domains[domainData.id].quotas);
      return;
    }
    try {
      const res = await billingService.getSubscriptionOverview('cdn', domainData.id);
      if (res.success) {
        setResourceQuota(res.data?.resource_quota_usage?.domains?.[domainData.id]?.quotas || null);
      }
    } catch (e) {
      // ignore; UI will simply not block by quota
    }
  };

  const handleAdd = () => {
    const ruleQuota = resourceQuota?.cdn_rules_limit;
    if (ruleQuota && !ruleQuota.is_unlimited) {
      const limit = ruleQuota.limit ?? ruleQuota.limit_value ?? -1;
      const used = ruleQuota.used ?? ruleQuota.current ?? rules.length;
      if (limit > -1 && used >= limit) {
        Modal.warning({
          title: t('Rule limit reached'),
          content: t('Your current plan supports up to {{limit}} rules. Please upgrade your plan or delete an existing rule before adding another.', { limit })
        });
        return;
      }
    }

    form.resetFields();
    setEditingRule(null);
    setMatcher({});
    setAction({});
    setModalVisible(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    const settings = rule.settings || {};

    form.setFieldsValue({
      name: rule.name,
      priority: rule.priority,
      enabled: rule.enabled !== undefined ? rule.enabled : true
    });

    // Load matcher and action from settings
    setMatcher(settings.matcher || {});
    setAction(settings.action || {});
    setModalVisible(true);
  };

  const handleDelete = async (ruleId) => {
    setLoading(true);
    try {
      const response = await cdnService.deleteRule(ruleId);

      if (response.success) {
        message.success(t('Deleted successfully'));
        loadRules();
        reloadDomain();
      } else {
        message.error(response.error || t('Deletion failed'));
      }
    } catch (error) {
      console.error('Failed to delete rule:', error);
      message.error(t('Deletion failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Validate that matcher and action are properly configured
      if (!matcher || !matcher.conditions || matcher.conditions.length === 0) {
        message.error(t('Please configure at least one match condition'));
        setLoading(false);
        return;
      }
      // 请求头/Cookie/查询参数必须带名称、比较算子必须有值:否则边缘编译该规则失败并静默跳过。
      const badIdx = matcher.conditions.findIndex((c) => (
        (['HEADER', 'COOKIE', 'QS_ARGS'].includes(c.collection) && !String(c.collection_var || '').trim())
        || (!['exists', 'not_exists'].includes(c.op) && (Array.isArray(c.var) ? c.var.length === 0 : String(c.var ?? '').trim() === ''))
      ));
      if (badIdx >= 0) {
        const c = matcher.conditions[badIdx];
        message.error(['HEADER', 'COOKIE', 'QS_ARGS'].includes(c.collection) && !String(c.collection_var || '').trim()
          ? t('Condition {{value}}: please enter {{named}}', { value: badIdx + 1, named: t('Parameter name') })
          : t('Condition {{value}}: please enter a value', { value: badIdx + 1 }));
        setLoading(false);
        return;
      }
      // 后端 RuleController 要求 action 非空，否则返回 400 原始报错。
      // 前置友好校验，避免用户配了条件却没配动作时看到生硬错误。
      // 清理空值(空串/空数组/无 body 的自定义响应 = 跟随域名)
      const cleanAction = serializeActions(action);
      if (Object.keys(cleanAction).length === 0) {
        message.error(t('Please configure at least one action'));
        setLoading(false);
        return;
      }
      const actionError = validateActions(cleanAction, t);
      if (actionError) {
        message.error(actionError);
        setLoading(false);
        return;
      }

      // Build settings object with matcher and action
      const settings = {
        matcher: matcher,
        action: cleanAction
      };

      const data = {
        domain_id: domainData.id,
        name: values.name,
        priority: values.priority || 100,
        settings: settings,
        enabled: values.enabled !== undefined ? values.enabled : true
      };

      let response;
      if (editingRule) {
        response = await cdnService.updateRule({
          ...data,
          id: editingRule.id
        });
      } else {
        response = await cdnService.createRule(data);
      }

      if (response.success) {
        message.success(editingRule ? t('Updated successfully') : t('Added successfully'));
        setModalVisible(false);
        loadRules();
        reloadDomain();
      } else {
        message.error(response.error || t('Operation failed'));
      }
    } catch (error) {
      console.error('Failed to save rule:', error);
      message.error(t('Operation failed'));
    } finally {
      setLoading(false);
    }
  };

  const actionLabel = (key) => (ACTION_LABELS[key] ? t(ACTION_LABELS[key]) : key);

  const columns = [
    {
      title: t('Rule name'),
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: t('Priority'),
      dataIndex: 'priority',
      key: 'priority',
      sorter: (a, b) => a.priority - b.priority,
      render: (priority) => <Tag color="blue">{priority}</Tag>
    },
    {
      title: t('Status'),
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? t('Enable') : t('Disable')}
        </Tag>
      )
    },
    {
      title: t('Match conditions'),
      key: 'matcher',
      render: (_, record) => {
        const matcher = record.settings?.matcher || {};
        const conditionCount = matcher.conditions?.length || 0;
        return (
          <Space>
            <Tag color="blue">{t('{{conditionCount}} conditions', { conditionCount })}</Tag>
            <Tag>{matcher.op === 'or' ? t('Match any') : t('Match all')}</Tag>
          </Space>
        );
      }
    },
    {
      title: t('Action settings'),
      key: 'action',
      ellipsis: true,
      render: (_, record) => {
        const action = record.settings?.action || {};
        const actions = Object.keys(action).filter(key => action[key] && typeof action[key] === 'object');
        return (
          <Space size={4} wrap>
            {actions.map(key => (
              <Tag key={key} color="cyan">{actionLabel(key)}</Tag>
            ))}
            {actions.length === 0 && <Tag>{t('No action')}</Tag>}
          </Space>
        );
      }
    },
    {
      title: t('Created at'),
      dataIndex: 'created_at',
      key: 'created_at'
    },
    {
      title: t('Actions'),
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >{t('Edit')}</Button>
          <Popconfirm
            title={t('Delete this rule?')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('OK')}
            cancelText={t('Cancel')}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>{t('Delete')}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Mobile card renderer
  const renderMobileCard = (rule) => (
    <Card
      key={rule.id}
      size="small"
      style={{ marginBottom: 12 }}
      extra={
        <Tag color={rule.enabled ? 'success' : 'default'}>
          {rule.enabled ? t('Enable') : t('Disable')}
        </Tag>
      }
    >
      <div style={{ marginBottom: 8 }}>
        <strong>{rule.name}</strong>
        <Tag color="blue" style={{ marginLeft: 8 }}>{t('Priority: {{priority}}', { priority: rule.priority })}</Tag>
      </div>
      <div style={{ marginBottom: 8 }}>
        <Space wrap size="small">
          <Tag color="blue">{rule.settings?.matcher?.conditions?.length || 0}{t(' conditions')}</Tag>
          <Tag>{rule.settings?.matcher?.op === 'or' ? t('Match any') : t('Match all')}</Tag>
          {Object.keys(rule.settings?.action || {}).filter(key => rule.settings?.action?.[key] && typeof rule.settings?.action?.[key] === 'object').map(key => (
            <Tag key={key} color="cyan">{actionLabel(key)}</Tag>
          ))}
        </Space>
      </div>
      <div style={{ fontSize: '12px', color: '#999', marginBottom: 8 }}>{t('Created: {{created_at}}', { created_at: rule.created_at })}</div>
      <Space wrap>
        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(rule)}>{t('Edit')}</Button>
        <Popconfirm
          title={t('Delete this rule?')}
          onConfirm={() => handleDelete(rule.id)}
          okText={t('OK')}
          cancelText={t('Cancel')}
        >
          <Button size="small" danger icon={<DeleteOutlined />}>{t('Delete')}</Button>
        </Popconfirm>
      </Space>
    </Card>
  );

  return (
    <div style={{ padding: isMobile ? '12px 0' : '24px 0' }}>
      {resourceQuota?.cdn_rules_limit && !resourceQuota.cdn_rules_limit.is_unlimited && (
        <Alert
          type={resourceQuota.cdn_rules_limit.used >= resourceQuota.cdn_rules_limit.limit ? 'error' : 'info'}
          message={t('Rule quota: {{used}}/{{limit}}{{value}}', { used: resourceQuota.cdn_rules_limit.used, limit: resourceQuota.cdn_rules_limit.limit, value: resourceQuota.cdn_rules_limit.used >= resourceQuota.cdn_rules_limit.limit ? t('(Limit reached)') : '' })}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Card
        title={
          <Space>
            <ColumnHeightOutlined />{t('Page rules')}</Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} size={isMobile ? 'small' : 'middle'}>
            {isMobile ? t('Add') : t('Add rule')}
          </Button>
        }
        size={isMobile ? 'small' : 'default'}
      >
        <Alert
          message={t('Configuration instructions')}
          description={isMobile ? t('Configure caching and optimization for different URLs') : t('Page rules let you configure caching, security, and optimization for different URL patterns. Lower priority numbers take precedence.')}
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />

        {isMobile ? (
          <div>
            {rules.map(rule => renderMobileCard(rule))}
            {rules.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>{t('No rules yet')}</div>
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={rules}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: <Empty description={t('No page rules yet. Click "Create rule" in the top right.')} /> }}
          />
        )}
      </Card>

      {/* 添加/编辑对话框 */}
      <Modal
        title={editingRule ? t('Edit rule') : t('Add rule')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={isMobile ? '100%' : 1200}
        style={{ top: isMobile ? 10 : 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            enabled: true,
            priority: 100
          }}
        >
          <Alert
            message={t('About rule configuration')}
            description={isMobile ? t('Configure match conditions and actions') : t('Page rules consist of match conditions and actions. When a request matches the conditions, the configured actions run. Lower priority numbers take precedence.')}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            icon={<InfoCircleOutlined />}
          />

          <Form.Item
            name="name"
            label={t('Rule name')}
            rules={[{ required: true, message: t('Enter a rule name') }]}
          >
            <Input placeholder={t('E.g. static asset cache rule')} />
          </Form.Item>

          <Space wrap style={{ width: '100%', marginBottom: 16 }}>
            <Form.Item name="priority" label={t('Priority')} extra={isMobile ? undefined : t('Lower values have higher priority')} style={{ marginBottom: 0 }}>
              <InputNumber min={1} max={999} style={{ width: isMobile ? 100 : 150 }} />
            </Form.Item>

            <Form.Item name="enabled" label={t('Enable')} valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
          </Space>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>{t('Match conditions')}</Divider>
          <ConditionBuilder value={matcher} onChange={setMatcher} />

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>{t('Action')}</Divider>
          <ActionBuilder
            value={action}
            onChange={setAction}
            locks={{
              cache: planAllows(domainData, 'advanced') ? null : 'advanced',
              forceCache: planAllows(domainData, 'full') ? null : 'full'
            }}
          />
        </Form>
      </Modal>
    </div>
  );
}
